import React, { useEffect, useState, useMemo } from 'react';
import {
  Card, ProductShort, ProductDiv, Button, ActionsDiv, FormGrid, FormGroup, Label,
  Select, HeaderLine, Td, Th, Tr, InputLabel, Summary, SummaryItem, SummaryLabel,
  SummaryValue, Separator, ButtonsDiv, AddButton, AddButtonImg, AddButtonText,
  CancelButton, CancelButtonText, RemoveButton, RemoveButtonImg,
  RemoveButtontext
} from './style';

import rectangle from '../../../../assets/fashion/TableFashionHeader/rectangle.svg';
import trash from '../../../../assets/fashion/TableFashionHeader/trash.svg';
import whitePlus from '../../../../assets/fashion/TableFashionHeader/whitePlus.svg';
import remove from '../../../../assets/fashion/Actions/remove.svg';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Outlet } from 'react-router-dom';
import axios from '../../../../config/axios';

// --- Interfaces de DTOs para o Frontend (Espelham o que o Backend espera) ---

// Define a estrutura de cada item de cor/tamanho que será enviado para o backend
interface FashionOrderProductItemInput {
  colorId: number;
  size: string;
  quantity: number;
}

// Define a estrutura completa de um objeto de cor, vindo da API de famílias
interface ColorData {
  id: number;
  name: string;
}

// Adapta a interface ProductApiData para a nova estrutura que será enviada/recebida
interface ProductApiData {
  id: number;
  fashionProductId: number;
  discount: number | null;
  totalItems: number;
  subtotal: number;
  items: FashionOrderProductItemInput[];
  size?: string[];

  fashionOrderId?: number;
  fashionProduct?: {
    id: number;
    name: string;
    price: number;
    size: string[];
    group: { id: number; name?: string; family?: { id: number; name?: string; collectionId?: number; } };
  };
  colorCount?: number;
  colors?: string[];
  family?: { id: number; Corileone?: { name: string }; name?: string; familyId?: number; };
  group?: { id: number; name?: string };
  name?: string;
  price?: number;
  totalQuantity?: number;
  totalSubtotal?: number;
  selectedColors?: number[];
}

// Interfaces de dados da API (como FamilyData e GroupData)

interface CollectionData {
  id: number;
  name: string;
}

interface FamilyData {
  id: number;
  name: string;
  collectionId?: number;
  groups?: GroupData[];
  colors?: ColorData[]; // Agora armazena objetos ColorData
}

interface GroupData {
  id: number;
  name: string;
  products?: ProductApiData[]; // Note que ProductApiData aqui ainda pode ter a estrutura antiga se a API de busca de produtos não foi atualizada
}

interface PropsType {
  order: any[];
  setOrder: React.Dispatch<React.SetStateAction<any>>;
  product?: any;
  onClose?: () => void;
  onRemoveProduct: (index: number) => void;
  onProductUpdated?: (oldProduct: ProductApiData, updatedProduct: ProductApiData) => void;
  onProductAdded?: any;
  isEditing: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const PedidoForm: React.FC<PropsType> = ({ order, setOrder, product, onClose, onRemoveProduct, onProductUpdated, onProductAdded, isEditing }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  // availableColors agora armazena objetos ColorData { id: number; name: string }
  const [availableColors, setAvailableColors] = useState<ColorData[]>([]);
  // selectedColors agora mapeia colorId -> boolean para as cores selecionadas na UI
  const [selectedColors, setSelectedColors] = useState<{ [colorId: number]: boolean }>({});
  // Quantities agora é um Map: colorId -> { size -> quantity }
  const [quantities, setQuantities] = useState<Map<number, { [size: string]: number }>>(new Map());
  const [discount, setDiscount] = useState<string>('');

  const [collectionsData, setCollectionsData] = useState<CollectionData[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [familiesData, setFamiliesData] = useState<FamilyData[]>([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>('');
  const [groupsData, setGroupsData] = useState<GroupData[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [productsData, setProductsData] = useState<ProductApiData[]>([]);

  // Calcula totalQuantity e totalSubtotal dinamicamente a partir do Map 'quantities'
  const calculatedTotalQuantity = useMemo(() => {
    let sum = 0;
    quantities.forEach(sizeMap => {
      Object.values(sizeMap).forEach(qty => sum += qty);
    });
    return sum;
  }, [quantities]);

  const totalSelectedColors = useMemo(() => Object.values(selectedColors).filter(Boolean).length, [selectedColors]);

  const calculatedTotalSubtotal = useMemo(() => {
    if (selectedPrice === null) return 0;
    const rawTotal = calculatedTotalQuantity * selectedPrice;
    const discountValue = parseFloat(discount);
    const discountAmount = !isNaN(discountValue) ? (discountValue / 100) * rawTotal : 0;
    return rawTotal - discountAmount;
  }, [calculatedTotalQuantity, selectedPrice, discount]);

  const availableSizes = useMemo(() => {
    if (!selectedProductId) return [];
    const selectedProduct = productsData.find((p: any) => p.id === selectedProductId);
    return selectedProduct?.size || [];
  }, [selectedProductId, productsData]);

  // 1. useEffect para buscar COLEÇÕES
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/collections`);
        if (Array.isArray(response.data)) {
          setCollectionsData(response.data);
        } else {
          console.error('Dados de coleções não são um array:', response.data);
          setCollectionsData([]);
        }
      } catch (error) {
        console.error('Erro ao carregar coleções:', error);
        setCollectionsData([]);
      }
    };
    fetchCollections();
  }, []);

  // 2. useEffect para buscar FAMÍLIAS quando a COLEÇÃO selecionada muda (funcionalidade atualizada)
  useEffect(() => {
    if (selectedCollectionId) {
      const fetchFamilies = async () => {
        try {
          const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/families?collectionId=${selectedCollectionId}`);
          if (Array.isArray(response.data)) {
            setFamiliesData(response.data);
          } else {
            console.error('Dados de famílias não são um array:', response.data);
            setFamiliesData([]);
          }
        } catch (error) {
          console.error('Erro ao carregar famílias:', error);
          setFamiliesData([]);
        }
      };
      fetchFamilies();
    } else {
      setFamiliesData([]);
      setSelectedFamilyId('');
      setSelectedGroupId('');
      setSelectedProductId(null);
      setSelectedPrice(null);
      setAvailableColors([]);
      setSelectedColors({});
      setQuantities(new Map());
    }
  }, [selectedCollectionId]);

  // 3. useEffect para filtrar GRUPOS quando a FAMÍLIA selecionada muda
  useEffect(() => {
    if (selectedFamilyId) {
      const fetchGroups = async () => {
        try {
          const groupsResponse = await axios.get(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/groups?familyId=${selectedFamilyId}`
          );

          if (Array.isArray(groupsResponse.data)) {
            setGroupsData(groupsResponse.data);
          } else {
            setGroupsData([]);
          }

          if (isEditing && product && product.fashionProduct?.group?.family?.id && String(product.fashionProduct.group.family.id) === selectedFamilyId) {
            const initialGroupId = product.fashionProduct?.group?.id ? String(product.fashionProduct.group.id) : '';
            setSelectedGroupId(initialGroupId);
          } else {
            setSelectedGroupId('');
            setSelectedProductId(null);
            setSelectedPrice(null);
            setAvailableColors([]);
            setSelectedColors({});
            setQuantities(new Map());
          }
        } catch (error) {
          console.error('Erro ao carregar grupos:', error);
          setGroupsData([]);
        }
      };
      fetchGroups();
    } else {
      setGroupsData([]);
      setAvailableColors([]);
      setProductsData([]);
      setSelectedGroupId('');
      setSelectedProductId(null);
      setSelectedPrice(null);
      setSelectedColors({});
      setQuantities(new Map());
    }
  }, [selectedFamilyId, isEditing, product]);

  // 4. useEffect para buscar PRODUTOS quando o GRUPO selecionado muda
  useEffect(() => {
    if (selectedGroupId) {
      const fetchProducts = async () => {
        try {
          const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product`);

          if (Array.isArray(response.data)) {
            const filteredProducts = response.data.filter(
              (prod: any) => prod.groupId === parseInt(selectedGroupId) && prod.status === true
            );
            setProductsData(filteredProducts);
          } else {
            setProductsData([]);
          }

          if (isEditing && product && product.fashionProduct?.group?.id && String(product.fashionProduct.group.id) === selectedGroupId) {
            setSelectedProductId(product.fashionProductId || product.fashionProduct?.id || null);
            setSelectedPrice(product.fashionProduct?.price || null);
            setDiscount(String(product.discount || ''));
          } else {
            setSelectedProductId(null);
            setSelectedPrice(null);
            setSelectedColors({});
            setQuantities(new Map());
          }
        } catch (error) {
          console.error('Erro ao carregar produtos:', error);
          setProductsData([]);
        }
      };
      fetchProducts();
    } else {
      setProductsData([]);
      setSelectedProductId(null);
      setSelectedPrice(null);
      setSelectedColors({});
      setQuantities(new Map());
    }
  }, [selectedGroupId, isEditing, product]);

  useEffect(() => {
    if (isEditing && product && collectionsData.length > 0) {
      const initialCollectionId = product.fashionProduct?.group?.family?.collectionId ? String(product.fashionProduct.group.family.collectionId) : '';
      const initialFamilyId = product.fashionProduct?.group?.family?.id ? String(product.fashionProduct.group.family.id) : '';

      setSelectedCollectionId(initialCollectionId);
      setSelectedFamilyId(initialFamilyId);
    } else if (!isEditing) {
      setSelectedCollectionId('');
      setSelectedProductId(null);
      setSelectedFamilyId('');
      setSelectedGroupId('');
      setSelectedPrice(null);
      setAvailableColors([]);
      setSelectedColors({});
      setQuantities(new Map());
      setDiscount('');
    }
  }, [isEditing, product, collectionsData]);

  const handleProductSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = Number(event.target.value);
    setSelectedProductId(selectedId);

    const selectedProduct = productsData.find(p => p.id === selectedId);
    if (selectedProduct) {
      setSelectedPrice(selectedProduct.price || null);
    } else {
      setSelectedPrice(null);
    }
    setQuantities(new Map());
    setSelectedColors({});
  };

  useEffect(() => {
    if (selectedProductId && selectedFamilyId) {
      const fetchColorsForProduct = async () => {
        try {
          const colorsResponse = await axios.get(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/families/${selectedFamilyId}/colors`
          );

          if (colorsResponse.data && Array.isArray(colorsResponse.data.colors)) {
            setAvailableColors(colorsResponse.data.colors);

            if (isEditing && product && product.items && Array.isArray(product.items)) {
              const initialSelectedColors: { [colorId: number]: boolean } = {};
              const initialQuantitiesMap = new Map<number, { [size: string]: number }>();

              product.items.forEach((item: any) => {
                const colorId = item.colorId;
                const size = item.size;
                const quantity = item.quantity;

                initialSelectedColors[colorId] = true;
                if (!initialQuantitiesMap.has(colorId)) {
                  initialQuantitiesMap.set(colorId, {});
                }
                initialQuantitiesMap.get(colorId)![size] = quantity;
              });

              setSelectedColors(initialSelectedColors);
              setQuantities(initialQuantitiesMap);
            }
          } else {
            setAvailableColors([]);
          }
        } catch (error) {
          console.error('Erro ao carregar cores:', error);
          setAvailableColors([]);
        }
      };
      fetchColorsForProduct();
    } else {
      setAvailableColors([]);
      if (!isEditing) {
        setSelectedColors({});
        setQuantities(new Map());
      }
    }
  }, [selectedProductId, selectedFamilyId, isEditing, product]);

  const handleAddProduct = () => {
    const selectedProduct = productsData.find((p) => p.id === selectedProductId);
    const selectedCollectionObj = collectionsData.find((c) => String(c.id) === selectedCollectionId);
    const selectedFamilyObj = familiesData.find((f) => String(f.id) === selectedFamilyId);
    const selectedGroupObj = groupsData.find((g) => String(g.id) === selectedGroupId);

    if (!selectedProduct || !selectedCollectionObj || !selectedFamilyObj || !selectedGroupObj || selectedPrice === null || totalSelectedColors === 0) {
      alert('Preencha todas as informações, incluindo pelo menos uma cor e quantidades.');
      return;
    }

    // Construir o array 'items' para enviar ao backend
    const itemsToSubmit: FashionOrderProductItemInput[] = [];
    let calculatedTotalItems = 0; // Para recalcular totalItems com base nos novos 'items'

    // Primeiro: adicionar itens com quantidades > 0
    quantities.forEach((sizeMap, colorId) => {
      // Encontra o objeto de cor para obter o nome para exibição
      const colorObj = availableColors.find(c => c.id === colorId);
      if (!colorObj) return; // Se a cor não for encontrada, pule.

      Object.entries(sizeMap).forEach(([sizeItem, quantity]) => {
        if (quantity > 0) {
          itemsToSubmit.push({
            colorId: colorId,
            size: sizeItem,
            quantity: quantity
          });
          calculatedTotalItems += quantity;
        }
      });
    });

    // Segundo: adicionar cores selecionadas sem quantidades (quantidade 0)
    Object.keys(selectedColors).forEach(colorIdStr => {
      const colorId = parseInt(colorIdStr);
      if (selectedColors[colorId]) {
        const hasItemsForColor = itemsToSubmit.some(item => item.colorId === colorId);

        // Se a cor está selecionada mas não tem itens, adiciona com quantidade 0
        if (!hasItemsForColor) {
          itemsToSubmit.push({
            colorId: colorId,
            size: "N/A", // Ou um tamanho padrão
            quantity: 0
          });
        }
      }
    });

    const newProductToAdd: ProductApiData = {
      id: 0,
      fashionProductId: selectedProduct.id,
      discount: parseFloat(discount),
      totalItems: calculatedTotalItems, // Usar o total calculado dos 'items'
      subtotal: calculatedTotalSubtotal, // Usar o subtotal calculado
      items: itemsToSubmit, // O novo array de itens de produto
      selectedColors: Object.keys(selectedColors)
        .filter(colorIdStr => selectedColors[parseInt(colorIdStr)])
        .map(colorIdStr => parseInt(colorIdStr)), // IDs das cores selecionadas

      // Propriedades para exibição no frontend (nomes das cores)
      colors: availableColors
        .filter(color => selectedColors[color.id])
        .map(color => color.name),

      fashionProduct: {
        id: selectedProduct.id,
        name: selectedProduct.name || '',
        price: selectedPrice,
        size: selectedProduct.fashionProduct?.size || [],
        group: {
          id: selectedGroupObj.id,
          name: selectedGroupObj.name,
          family: {
            id: selectedFamilyObj.id,
            name: selectedFamilyObj.name,
            collectionId: selectedCollectionObj.id
          }
        }
      },
      family: {
        id: selectedFamilyObj.id,
        name: selectedFamilyObj.name,
        Corileone: { name: selectedFamilyObj.name },
        familyId: selectedFamilyObj.id
      },
      group: {
        id: selectedGroupObj.id,
        name: selectedGroupObj.name
      },
      fashionOrderId: product?.fashionOrderId
    };

    if (onProductAdded) {
      onProductAdded(newProductToAdd);
    } else {
      console.warn("PedidoForm: A prop 'onProductAdded' não foi fornecida. Usando 'setOrder' como fallback.");
      setOrder((prev: any[]) => [...prev, newProductToAdd]);
    }

    setSelectedProductId(null);
    setSelectedCollectionId('');
    setSelectedFamilyId('');
    setSelectedGroupId('');
    setSelectedPrice(null);
    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());
    setDiscount('');
    onClose?.();
  };

  const handleUpdateProduct = () => {
    const selectedProductData = productsData.find(p => p.id === selectedProductId);
    const selectedCollectionObj = collectionsData.find((c) => String(c.id) === selectedCollectionId);
    const selectedFamilyObj = familiesData.find(f => String(f.id) === selectedFamilyId);
    const selectedGroupObj = groupsData.find(g => String(g.id) === selectedGroupId);

    if (!selectedProductData || !selectedCollectionObj || !selectedFamilyObj || !selectedGroupObj || selectedPrice === null || totalSelectedColors === 0) {
      alert('Por favor, selecione uma família, coleção, produto, pelo menos uma cor e quantidades válidas.');
      return;
    }

    // Construir o array 'items' para enviar ao backend para atualização
    const itemsToSubmit: FashionOrderProductItemInput[] = [];
    let calculatedTotalItems = 0; // Para recalcular totalItems com base nos novos 'items'

    // Primeiro: adicionar itens com quantidades > 0
    quantities.forEach((sizeMap, colorId) => {
      Object.entries(sizeMap).forEach(([sizeItem, quantity]) => {
        if (quantity > 0) {
          itemsToSubmit.push({
            colorId: colorId,
            size: sizeItem,
            quantity: quantity
          });
          calculatedTotalItems += quantity;
        }
      });
    });

    // Segundo: adicionar cores selecionadas sem quantidades (quantidade 0)
    Object.keys(selectedColors).forEach(colorIdStr => {
      const colorId = parseInt(colorIdStr);
      if (selectedColors[colorId]) {
        const hasItemsForColor = itemsToSubmit.some(item => item.colorId === colorId);

        // Se a cor está selecionada mas não tem itens, adiciona com quantidade 0
        if (!hasItemsForColor) {
          itemsToSubmit.push({
            colorId: colorId,
            size: "N/A", // Ou um tamanho padrão
            quantity: 0
          });
        }
      }
    });

    const updatedProduct: ProductApiData = {
      ...(product as ProductApiData), // Mantém propriedades do produto original

      fashionProductId: selectedProductData.id,
      discount: parseFloat(discount),
      totalItems: calculatedTotalItems, // Usar o total calculado dos 'items'
      subtotal: calculatedTotalSubtotal, // Usar o subtotal calculado
      items: itemsToSubmit, // O novo array de itens de produto para atualização
      selectedColors: Object.keys(selectedColors)
        .filter(colorIdStr => selectedColors[parseInt(colorIdStr)])
        .map(colorIdStr => parseInt(colorIdStr)), // IDs das cores selecionadas

      // Propriedades para exibição no frontend (nomes das cores)
      colors: availableColors
        .filter(color => selectedColors[color.id])
        .map(color => color.name),

      fashionProduct: {
        ...(product?.fashionProduct),
        id: selectedProductData?.id || 0,
        name: selectedProductData?.name || '',
        price: selectedPrice,
        group: {
          id: selectedGroupObj.id,
          name: selectedGroupObj.name,
          family: {
            id: selectedFamilyObj.id,
            name: selectedFamilyObj.name,
            collectionId: selectedCollectionObj.id
          }
        }
      },
      family: {
        id: selectedFamilyObj.id,
        name: selectedFamilyObj.name,
        Corileone: { name: selectedFamilyObj.name },
        familyId: selectedFamilyObj.id
      },
      group: {
        id: selectedGroupObj.id,
        name: selectedGroupObj.name
      }
    };

    if (onProductUpdated) {
      onProductUpdated(product, updatedProduct);
    }

    onClose?.();
  };

  const handleRemoveProductLocal = (indexToRemove: number) => {
    onRemoveProduct(indexToRemove);
    onClose?.();
  };

  return (
    <>

      {isOpen && (
        <Card>
          <FormGrid className="grid grid-cols-4 gap-4 mt-4">
            <FormGroup>
              <Label>Coleção</Label>
              <Select
                value={selectedCollectionId ?? ''}
                onChange={(e) => {
                  setSelectedCollectionId(e.target.value);
                }}
              >
                <option value="">Selecione a Coleção</option>
                {Array.isArray(collectionsData) && collectionsData.map((collection) => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Família</Label>
              <Select
                value={selectedFamilyId ?? ''}
                onChange={(e) => {
                  setSelectedFamilyId(e.target.value);
                }}
                disabled={!selectedCollectionId || !Array.isArray(familiesData) || familiesData.length === 0}
              >
                <option value="">Selecione a Família</option>
                {Array.isArray(familiesData) && familiesData.map((family) => (
                  <option key={family.id} value={family.id}>
                    {family.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Grupo</Label>
              <Select
                value={selectedGroupId ?? ''}
                onChange={(e) => {
                  setSelectedGroupId(e.target.value);
                }}
                disabled={!selectedFamilyId || !Array.isArray(groupsData) || groupsData.length === 0}
              >
                <option value="">Selecione o Grupo</option>
                {Array.isArray(groupsData) && groupsData.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Produto</Label>
              <Select
                value={selectedProductId ?? ''}
                onChange={handleProductSelectChange}
                disabled={!selectedGroupId || !Array.isArray(productsData) || productsData.length === 0}
              >
                <option value="">Selecione o Produto</option>
                {Array.isArray(productsData) && productsData.map((prod: any) => (
                  <option key={prod.id} value={prod.id}>
                    {prod.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Desconto %</Label>
              <InputLabel
                type="number"
                step="0.1"
                min="0"
                max="100"
                className="w-full rounded bg-gray-100 p-2"
                value={discount}
                onChange={(e) => setDiscount(String(e.target.value))}
              />
            </FormGroup>

            <FormGroup>
              <Label className="block text-gray-500">Precificação</Label>
              <div style={{ 'padding': '10px', 'fontWeight': '700', 'color': ' #303030' }}>
                {formatCurrency(selectedPrice || 0)}
              </div>
            </FormGroup>
          </FormGrid>

          {/* Tabela de Cores e Quantidades */}
          {selectedProductId && availableColors.length > 0 && availableSizes.length > 0 && (
            <>
              <thead>
                <HeaderLine>
                  <Th></Th>
                  {availableSizes.map((item: string, index: number) => (
                    <Th key={index}>{item}</Th>
                  ))}
                </HeaderLine>
              </thead>
              <tbody>
                {availableColors.map((colorItem) => (
                  <Tr key={colorItem.id}>
                    <Td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={!!selectedColors[colorItem.id]}
                          onChange={() => {
                            setSelectedColors((prev) => {
                              const newSelected = {
                                ...prev,
                                [colorItem.id]: !prev[colorItem.id],
                              };
                              if (prev[colorItem.id]) {
                                setQuantities((prevQuantitiesMap) => {
                                  const updatedMap = new Map(prevQuantitiesMap);
                                  updatedMap.delete(colorItem.id);
                                  return updatedMap;
                                });
                              }
                              return newSelected;
                            });
                          }}
                        />
                        {colorItem.name}
                      </div>
                    </Td>
                    {availableSizes.map((sizeItem: string, sizeIndex: number) => (
                      <Td key={sizeIndex}>
                        <InputLabel
                          type='number'
                          min='0'
                          disabled={!selectedColors[colorItem.id]}
                          value={quantities.get(colorItem.id)?.[sizeItem] || '0'}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            setQuantities((prevQuantitiesMap) => {
                              const updatedMap = new Map(prevQuantitiesMap);
                              if (!updatedMap.has(colorItem.id)) {
                                updatedMap.set(colorItem.id, {});
                              }
                              updatedMap.get(colorItem.id)![sizeItem] = isNaN(value) ? 0 : value;
                              return updatedMap;
                            });
                          }}
                        />
                      </Td>
                    ))}
                  </Tr>
                ))}
              </tbody>
            </>
          )}

          <Separator />

          {/* Resumo de Cores, Quantidade de Peças, Subtotal */}
          <Summary>
            <SummaryItem>
              <SummaryLabel className="text-gray-500">Cores:</SummaryLabel>
              <SummaryValue>{totalSelectedColors}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel className="text-gray-500">Quantidade de peças:</SummaryLabel>
              <SummaryValue>{calculatedTotalQuantity}</SummaryValue> {/* Usar calculatedTotalQuantity */}
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel className="text-gray-500">Subtotal:</SummaryLabel>
              <SummaryValue>
                {formatCurrency(calculatedTotalSubtotal)} {/* Usar calculatedTotalSubtotal formatado*/}
              </SummaryValue>
            </SummaryItem>
          </Summary>

          <Separator />

          {/* Botões de Ação */}
          <ButtonsDiv>
            {isEditing ? (
              <AddButton onClick={handleUpdateProduct}>
                <AddButtonImg src={whitePlus} />
                <AddButtonText>Salvar Alterações</AddButtonText>
              </AddButton>
            ) : (
              <AddButton onClick={handleAddProduct}>
                <AddButtonImg src={whitePlus} />
                <AddButtonText>Adicionar ao pedido</AddButtonText>
                <Outlet context={{ addedProducts: order, setAddedProducts: setOrder }} />
              </AddButton>
            )}
            <CancelButton onClick={() => onClose?.()}>
              <CancelButtonText>Cancelar</CancelButtonText>
            </CancelButton>
            {isEditing && (
              <RemoveButton onClick={() => onRemoveProduct(order.findIndex(p => p.fashionProductId === product?.fashionProductId))}>
                <RemoveButtonImg src={remove} />
                <RemoveButtontext>Remover</RemoveButtontext>
              </RemoveButton>
            )}
          </ButtonsDiv>
        </Card>
      )}
    </>
  );
};

export default PedidoForm;